// A patient the import couldn't create, shown by PatientImportReview.openUncreated() from the review list. Their
// record in the previous system had no usable name and date of birth, so the data migration kept them aside with
// their records (one PDF per episode of care) rather than lose them. Staff check the records to work out who it is,
// then add them to either:
//  - a patient already in CatTrax, found by search - even an imported patient still waiting for review; or
//  - a new patient, registered here with the full required details (the same patientDetailsForm the dashboard uses),
//    started from what the previous system did hold.
// The server files the records as the import would have and records where they went, so a later import run sends
// anything new for the same patient there too. Closes with { patient }: the patient the records went to.
//
// Each record is downloaded through CatTrax and read in the PDF viewer here, in place of the patient's details.
//
// PatientImportReview loads the patient before showing this; uncreated is null when it couldn't be loaded.
app.controller("patientImportUncreatedController", [
	"$scope",
	"$q",
	"$timeout",
	"$mdDialog",
	"Session",
	"countryConfig",
	"CurrentPatient",
	"PatientAPI",
	"PatientImportAPI",
	"PatientDuplicate",
	"PatientRequiredDetails",
	"uncreatedId",
	"uncreated",
	function (
		$scope,
		$q,
		$timeout,
		$mdDialog,
		Session,
		countryConfig,
		CurrentPatient,
		PatientAPI,
		PatientImportAPI,
		PatientDuplicate,
		PatientRequiredDetails,
		uncreatedId,
		uncreated
	) {
		// The pipeline's names for what a patient couldn't be created without.
		const IDENTITY_LABELS = { first_name: "First name", last_name: "Last name", dob: "Date of birth" };

		// Previous systems keep dates of birth in their own formats; one that fits none is shown as it came, unless it
		// is a placeholder with no date in it at all ("NULL").
		const DOB_FORMATS = ["YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss", "DD/MM/YYYY", "D/M/YYYY", "YYYY/MM/DD", "DD-MM-YYYY", "D-M-YYYY", "DD.MM.YYYY"];

		$scope.busy = false;
		$scope.error = null;
		// The details <form> publishes itself here: it sits inside an ng-if, whose scope the actions can't see.
		$scope.forms = {};
		$scope.missing = [];
		$scope.search = { term: "", loading: false, searched: false, results: [] };
		// What the verified pills compare a new patient's contacts against: nothing has been saved yet.
		$scope.unsaved = {};

		$scope.canPromote = Session.hasPermission("PATIENT_IMPORT_UNCREATED_PROMOTE");
		$scope.canSearch = $scope.canPromote && Session.hasPermission("PATIENT_SEARCH");
		$scope.canRegister = $scope.canPromote && Session.hasPermission("PATIENT_REGISTER");
		$scope.canViewRecords = Session.hasPermission("PATIENT_IMPORT_UNCREATED_RECORD_VIEW");
		// The record being read, when one is: { index, document, url, error }. The url is the downloaded PDF's, once
		// it has come.
		$scope.viewer = null;

		// The details form's NHI lookup marks CurrentPatient as a new patient being registered, and its fields follow
		// those flags. They are the dashboard's, which may be open behind the dialog, so they are cleared for the
		// dialog and put back as they were when it closes.
		const currentPatientFlags = {
			isPatientNew: CurrentPatient.isPatientNew,
			isPresetNewPatient: CurrentPatient.isPresetNewPatient,
		};
		CurrentPatient.isPresetNewPatient = false;

		let detailsForm = null;
		let duplicateCheck = null;

		$scope.$on("$destroy", function () {
			CurrentPatient.isPatientNew = currentPatientFlags.isPatientNew;
			CurrentPatient.isPresetNewPatient = currentPatientFlags.isPresetNewPatient;
			$timeout.cancel(duplicateCheck);
			releaseRecord();
		});

		$scope.registerDetailsForm = function (form) {
			detailsForm = form;
		};

		// What is still needed follows the edits as they are made, so registering waits until they are complete.
		$scope.$watch(
			() => ($scope.draft ? PatientRequiredDetails.missing($scope.draft, detailsForm) : []),
			(missing) => {
				$scope.missing = missing;
			},
			true
		);

		// Every required detail is filled in: the shared list, and the form's own required fields (name, date of
		// birth) and formats.
		$scope.detailsComplete = function () {
			const form = $scope.forms.newPatient;
			return !$scope.missing.length && !!form && form.$valid;
		};

		$scope.close = function () {
			$mdDialog.cancel();
		};

		function start() {
			if (!uncreated) {
				$scope.step = "error";
				$scope.error = "The patient couldn't be loaded. Close this and try again.";
				return;
			}

			$scope.uncreated = uncreated;
			const identity = uncreated.identity || {};
			$scope.source = titleCase(uncreated.source_system) || "the previous system";
			$scope.name = [identity.first_name, identity.last_name].map(clean).filter(Boolean).join(" ");
			$scope.dob = formatSourceDob(identity.date_of_birth);
			$scope.notCreatedWithout = (uncreated.missing_required || []).map((key) => IDENTITY_LABELS[key] || titleCase(key));
			$scope.importedFrom = ["Imported", uncreated.source_system ? "from " + $scope.source : null].filter(Boolean).join(" ")
				+ (moment(uncreated.imported_date).isValid() ? " · " + moment(uncreated.imported_date).locale("en").format("D MMM YYYY") : "");
			$scope.sourceAddress = formatSourceAddress(identity.address);
			$scope.facts = [
				{ label: "Preferred name", value: clean(identity.preferred_name) },
				{ label: countryConfig.countryCode === "AU" ? "IHI" : "NHI", value: clean(countryConfig.countryCode === "AU" ? identity.ihi : identity.nhi) },
				{ label: "Medicare", value: clean(identity.medicare_number) },
				{ label: "DVA", value: clean(identity.dva_number) },
				{ label: "Gender", value: titleCase(identity.gender) },
				{ label: "Mobile", value: clean(identity.mobile_number) },
				{ label: "Phone", value: clean(identity.phone_number) },
				{ label: "Email", value: clean(identity.email) },
				{ label: "Address", value: $scope.sourceAddress },
			].filter((fact) => fact.value);
			$scope.sections = (uncreated.record_sections || [])
				.filter((section) => section.rows)
				.map((section) => titleCase(section.section) + " " + section.rows)
				.join(" · ");
			setDocuments(uncreated.documents);
			// The search matches last names, NHIs, phone numbers and emails, but not first names.
			$scope.search.term = [identity.last_name, identity.nhi, identity.mobile_number, identity.phone_number, identity.email]
				.map(clean)
				.find(Boolean) || "";

			if (uncreated.status === "PROMOTED") {
				$scope.step = "done";
			} else if (uncreated.status === "PROMOTING") {
				// An earlier attempt stopped part-way: only finishing it into the same patient is offered.
				$scope.resume = { patientId: uncreated.promoted_patient_id, newPatient: !!uncreated.promoted_as_new_patient };
				$scope.step = "resume";
			} else {
				$scope.step = "choose";
			}
		}

		// In the server's order: a record is fetched by its place among them.
		function setDocuments(documents) {
			$scope.documents = (documents || []).map(function (document) {
				return {
					date: formatDate(document.clinic_date) || "Undated",
					name: document.file_name,
					viewable: document.viewable !== false,
				};
			});
		}

		// ---- Reading the records ------------------------------------------------------------------------------------

		$scope.canView = function (index) {
			const document = ($scope.documents || [])[index];
			return $scope.canViewRecords && !!document && document.viewable;
		};

		$scope.viewRecord = function (index) {
			if (!$scope.canView(index)) {
				return;
			}
			releaseRecord();
			const viewer = { index: index, document: $scope.documents[index], url: null, error: null };
			$scope.viewer = viewer;
			PatientImportAPI.getUncreatedDocument(uncreatedId, index)
				.then(function (resp) {
					// Another record may have been opened, or this one closed, while it downloaded.
					if ($scope.viewer === viewer) {
						viewer.url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
					}
				})
				.catch(function () {
					viewer.error = "This record couldn't be loaded. Try again.";
				});
		};

		$scope.closeRecord = function () {
			releaseRecord();
			$scope.viewer = null;
		};

		function releaseRecord() {
			if ($scope.viewer && $scope.viewer.url) {
				URL.revokeObjectURL($scope.viewer.url);
			}
		}

		// ---- Finding the patient already in CatTrax -----------------------------------------------------------------

		$scope.startExisting = function () {
			$scope.error = null;
			$scope.step = "existing";
			if ($scope.search.term && !$scope.search.searched) {
				$scope.runSearch();
			}
		};

		$scope.runSearch = function () {
			const term = clean($scope.search.term);
			if (!term || $scope.search.loading) {
				return;
			}
			$scope.search.loading = true;
			$scope.error = null;
			PatientAPI.search(null, null, term, null)
				.then(function (resp) {
					$scope.search.results = (resp.data || []).map(toCandidate);
					$scope.search.searched = true;
				})
				.catch(function () {
					$scope.error = "The search didn't work. Try again.";
				})
				.finally(function () {
					$scope.search.loading = false;
				});
		};

		function toCandidate(summary) {
			return {
				summary: summary,
				name: [summary.firstName, summary.lastName].filter(Boolean).join(" "),
				dob: formatDate(summary.dateOfBirth),
				practiceName: summary.practice && summary.practice.name,
				needsReview: !!summary.needs_review,
				canAdd: canAt(summary.practice, "PATIENT_IMPORT_UNCREATED_PROMOTE"),
			};
		}

		// Permissions are per practice, and the user only knows those of practices they work at.
		function canAt(practice, permission) {
			if (!practice) {
				return false;
			}
			const known = (Session.practiceOptions || []).find((p) => p.id === practice.id);
			if (known) {
				return Session.hasPermissionAt(known, permission);
			}
			return !!Session.practice && Session.practice.id === practice.id && Session.hasPermission(permission);
		}

		$scope.chooseCandidate = function (candidate) {
			if (!candidate.canAdd || $scope.busy) {
				return;
			}
			$scope.error = null;
			$scope.cameFrom = $scope.step;
			$scope.target = candidate;
			$scope.step = "confirm";
		};

		$scope.backFromConfirm = function () {
			$scope.error = null;
			$scope.step = $scope.cameFrom || "existing";
		};

		$scope.addToTarget = function () {
			if ($scope.target) {
				promote($scope.target.summary.id, false).catch(angular.noop);
			}
		};

		$scope.backToChoose = function () {
			$scope.error = null;
			$scope.step = "choose";
		};

		// ---- Registering a new patient ------------------------------------------------------------------------------

		$scope.startNewPatient = function () {
			$scope.error = null;
			if (!$scope.draft) {
				$scope.draft = draftFromIdentity(uncreated.identity || {});
			}
			$scope.step = "details";
		};

		// The patient already registered with the name and date of birth being typed, offered instead: registering
		// them again would be refused.
		$scope.$watchGroup(
			[
				"draft.firstName",
				"draft.lastName",
				"draft.nhi",
				() => showingDate().DD,
				() => showingDate().MM,
				() => showingDate().YYYY,
			],
			function () {
				if ($scope.step !== "details" || $scope.created) {
					return;
				}
				$timeout.cancel(duplicateCheck);
				duplicateCheck = $timeout(checkForDuplicate, 500);
			}
		);

		function showingDate() {
			return (detailsForm && detailsForm.getShowingDate()) || {};
		}

		function checkForDuplicate() {
			const draft = $scope.draft || {};
			const date = showingDate();
			if (!clean(draft.firstName) || !clean(draft.lastName) || !date.DD || !date.MM || String(date.YYYY || "").length !== 4) {
				$scope.duplicate = null;
				return;
			}
			const key = [draft.firstName, draft.lastName, draft.nhi, date.YYYY, date.MM, date.DD].join("|");
			PatientDuplicate.findExisting(draft.firstName, draft.lastName, date.YYYY + "/" + date.MM + "/" + date.DD, draft.nhi || "").then(
				function (existing) {
					const now = $scope.draft || {};
					const nowDate = showingDate();
					// The form may have moved on while the lookup was in flight.
					if (key !== [now.firstName, now.lastName, now.nhi, nowDate.YYYY, nowDate.MM, nowDate.DD].join("|")) {
						return;
					}
					$scope.duplicate = existing ? toCandidate(existing) : null;
				}
			);
		}

		$scope.registerAndAdd = function () {
			if ($scope.busy || !$scope.canRegister || !$scope.detailsComplete()) {
				return;
			}
			$scope.error = null;

			// Writes the address picked from the lookup and the date of birth onto the patient; an NZ address typed
			// rather than picked stops here, with the field selected and flagged.
			if (!detailsForm || !detailsForm.prepareForCreate()) {
				$scope.error = countryConfig.countryCode === "NZ"
					? "Choose the patient's address from the address lookup, so their DHB can be found."
					: "Check the patient's address.";
				return;
			}

			$scope.draft.practice = { id: Session.practice.id };
			$scope.busy = true;
			PatientAPI.create($scope.draft)
				.then(
					function (resp) {
						$scope.created = resp.data;
						return promote(resp.data.id, true).catch(function () {
							// Registered, but the records haven't gone yet: trying again only adds them.
							$scope.resume = {
								patientId: resp.data.id,
								newPatient: true,
								name: [resp.data.firstName, resp.data.lastName].filter(Boolean).join(" "),
							};
							$scope.step = "resume";
						});
					},
					function (resp) {
						$scope.error = resp && resp.status === 409
							? "A patient with this name and date of birth is already registered. Add the records to them instead."
							: registrationErrorMessage(resp);
					}
				)
				.finally(function () {
					$scope.busy = false;
				});
		};

		function registrationErrorMessage(resp) {
			const data = resp && resp.data;
			if (data && Array.isArray(data.error)) {
				return "Some details were rejected: " + data.error.join("; ");
			}
			return (data && (data.message || (typeof data.error === "string" && data.error))) || "The patient couldn't be registered. Check their details and try again.";
		}

		// What the previous system held, as a patient to register: the form checks and completes the rest.
		function draftFromIdentity(identity) {
			const draft = {};
			const set = (field, value) => {
				if (clean(value)) {
					draft[field] = clean(value);
				}
			};
			set("firstName", identity.first_name);
			set("lastName", identity.last_name);
			set("preferredName", identity.preferred_name);
			set("email", identity.email);
			set("mobileNumber", identity.mobile_number);
			set("phoneNumber", identity.phone_number);
			if (countryConfig.countryCode === "NZ" && clean(identity.nhi)) {
				draft.nhi = clean(identity.nhi).toUpperCase();
			}
			const dob = parseSourceDob(identity.date_of_birth);
			if (dob) {
				draft.dateOfBirth = new Date(dob.year(), dob.month(), dob.date());
			}
			const gender = String(identity.gender || "").trim().toUpperCase();
			if (gender === "M" || gender === "MALE") {
				draft.gender = "MALE";
			} else if (gender === "F" || gender === "FEMALE") {
				draft.gender = "FEMALE";
			}
			return draft;
		}

		// ---- Adding the records -------------------------------------------------------------------------------------

		$scope.finishAdding = function () {
			if ($scope.resume) {
				promote($scope.resume.patientId, $scope.resume.newPatient).catch(angular.noop);
			}
		};

		function promote(patientId, newPatient) {
			$scope.busy = true;
			$scope.error = null;
			return PatientImportAPI.promoteUncreated(uncreatedId, patientId, newPatient)
				.then(
					function (resp) {
						$mdDialog.hide({ patient: resp.data });
					},
					function (resp) {
						$scope.error = (resp && resp.data && resp.data.message) || "The records weren't added. Try again.";
						return $q.reject(resp);
					}
				)
				.finally(function () {
					$scope.busy = false;
				});
		}

		// ---- Formatting ---------------------------------------------------------------------------------------------

		function clean(value) {
			return value === undefined || value === null ? "" : String(value).trim();
		}

		function titleCase(value) {
			if (!clean(value)) {
				return "";
			}
			return clean(value)
				.toLowerCase()
				.split(/[_\s]+/)
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(" ");
		}

		function formatDate(value) {
			return value && moment(value).isValid() ? moment(value).locale("en").format("D MMM YYYY") : "";
		}

		function parseSourceDob(value) {
			const parsed = clean(value) ? moment(clean(value), DOB_FORMATS, true) : null;
			return parsed && parsed.isValid() && parsed.year() >= 1900 && !parsed.isAfter(moment()) ? parsed : null;
		}

		function formatSourceDob(value) {
			const parsed = parseSourceDob(value);
			if (parsed) {
				return parsed.locale("en").format("D MMM YYYY");
			}
			return /\d/.test(clean(value)) ? clean(value) : "";
		}

		// The previous system's address, as text: it may have come as its parts in JSON.
		function formatSourceAddress(value) {
			const text = clean(value);
			if (!text || text.charAt(0) !== "{") {
				return text;
			}
			try {
				const parts = JSON.parse(text);
				return Object.keys(parts).map((key) => clean(parts[key])).filter(Boolean).join(", ");
			} catch (e) {
				return text;
			}
		}

		$scope.initials = function () {
			return ($scope.name || "")
				.split(" ")
				.filter(Boolean)
				.slice(0, 2)
				.map((part) => part.charAt(0).toUpperCase())
				.join("");
		};

		start();
	},
]);
